import React, { useState } from "react";
import { endPoints, DefaultImageBaseurl } from "../../constant/Environment";
import { addData, getAllData } from "../../Utility/API";
import { Button, Form } from "react-bootstrap";
import Loader from "../../components/Loader";
import { ErrorToast, SuccesToast } from "../../components/Toast/message";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import PrizeTable from "./PrizeTable";
import SteepnessHelp from "./SteepnessHelp";
// Protection settings are switched off for now.
// import ProtectionFields from "./ProtectionFields";
import TournamentGuide from "./TournamentGuide";
import TournamentSummary from "./TournamentSummary";
import { tokens } from "../../theme";
import { DEFAULT_STEEPNESS, prizeTable } from "./prizeDistribution";

/** Numbered heading that splits the form into steps. */
const Section = ({ number, title, hint }) => (
  <div style={{ marginTop: "28px", paddingTop: "14px", borderTop: `1px solid ${tokens.border}` }}>
    <div style={{ fontWeight: 700, fontSize: "15px", color: tokens.text }}>
      {number}. {title}
    </div>
    {hint && <div style={{ fontSize: "12px", color: tokens.textDim }}>{hint}</div>}
  </div>
);

// Scoring the backend supports today. Sum Multiplier needs each win matched to
// its bet, which is still being researched.
const WIN_MECHANICS = [
  "Total Win",
  "Total Bet",
  // "Total Spin",
  // "Sum Multiplier",
  // "Highest Total Win Multiplier",
];

/** The API sends an ISO instant, the picker needs local "YYYY-MM-DDTHH:mm". */
const toPickerValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return String(value).substring(0, 16);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

/** "" means the protection is off and is sent as null. */
const numberOrNull = (value) => (value === "" || value === null || value === undefined ? null : parseFloat(value));
const asText = (value) => (value === null || value === undefined ? "" : String(value));

const emptyForm = {
  name: "",
  provider: "",
  winMechanic: "",
  startDate: "",
  endDate: "",
  minBet: "",
  prizePool: "",
  winnerCount: "",
  prizeSteepness: String(DEFAULT_STEEPNESS),
  leaderboardUrl: "",
  bannerUrl: "",
  isActive: true,
  // FIXED: players share exactly the Prize Pool. DYNAMIC: the pool is a % of bets, up to the Prize Pool.
  poolType: "FIXED",
  poolGrowthPercent: "",
  // 0 = no minimum.
  minTotalBet: "0",
  maxPrizeMultiplier: "",
  winnerPercent: "",
  poolTiers: [],
};

/** readOnly: results are saved (or paid), so the form only shows the settings. */
const CreateTournament = ({ open, onClose, editData, readOnly = false }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  // Counted SC bets of all joined players so far; a new tournament has none.
  const [totalBets, setTotalBets] = useState(0);
  // Protection settings are switched off for now.
  // Protection is folded away unless the tournament already uses it.
  // const [showProtection, setShowProtection] = useState(false);

  // Same standings the Players & Payout dialog shows; each player carries their total bet.
  React.useEffect(() => {
    setTotalBets(0);
    if (!open || !editData?.id) return;
    getAllData(`${endPoints.api.TOURNAMENT_ADMIN}/${editData.id}/players`).then((response) => {
      if (response?.status === "success" && Array.isArray(response.data)) {
        setTotalBets(response.data.reduce((sum, player) => sum + (player.totalBet || 0), 0));
      }
    });
  }, [editData, open]);

  React.useEffect(() => {
    if (!open) return;
    setErrors({});
    if (!editData?.id) {
      setForm(emptyForm);
      // setShowProtection(false);
      return;
    }
    // setShowProtection(
    //   editData.minTotalBet != null || editData.maxPrizeMultiplier != null ||
    //     editData.winnerPercent != null || !!editData.poolTiers?.length
    // );
    setForm({
      name: editData.name || "",
      provider: editData.provider || "",
      winMechanic: editData.winMechanic || "",
      startDate: toPickerValue(editData.startDate),
      endDate: toPickerValue(editData.endDate),
      minBet: editData.minBet ?? "",
      prizePool: editData.prizePool ?? "",
      winnerCount: editData.winnerCount ?? "",
      prizeSteepness: String(editData.prizeSteepness ?? DEFAULT_STEEPNESS),
      leaderboardUrl: editData.leaderboardUrl || "",
      bannerUrl: editData.bannerUrl || "",
      isActive: editData.isActive === 1,
      poolType: editData.poolGrowthPercent != null ? "DYNAMIC" : "FIXED",
      poolGrowthPercent: asText(editData.poolGrowthPercent),
      minTotalBet: asText(editData.minTotalBet ?? 0),
      maxPrizeMultiplier: asText(editData.maxPrizeMultiplier),
      winnerPercent: asText(editData.winnerPercent),
      poolTiers: (editData.poolTiers || []).map((tier) => ({
        minPlayers: asText(tier.minPlayers),
        pool: asText(tier.pool),
      })),
    });
  }, [editData, open]);

  const setField = (field, value) => {
    if (readOnly) return;
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Name is required";
    // Provider is no longer asked for.
    // if (!form.provider.trim()) next.provider = "Provider is required";
    if (!form.winMechanic) next.winMechanic = "Pick a win mechanic";
    if (!form.startDate) next.startDate = "Start date is required";
    if (!form.endDate) next.endDate = "End date is required";
    if (form.startDate && form.endDate && new Date(form.endDate) <= new Date(form.startDate)) {
      next.endDate = "End must be after start";
    }
    if (!(parseFloat(form.prizePool) > 0)) next.prizePool = "Enter a prize pool greater than 0";
    const winners = parseInt(form.winnerCount, 10);
    if (!(winners > 0 && winners <= 10000)) next.winnerCount = "Enter how many players win (1 to 10,000)";
    const steepness = parseFloat(form.prizeSteepness);
    if (!(steepness >= 0 && steepness <= 2)) next.prizeSteepness = "Enter a value from 0 to 2 (0.75 is like Evoplay)";
    validateProtections(next);
    // Banner is optional: the player site shows a fallback image without one.
    // Leaderboard link is not asked for any more.
    // if (form.leaderboardUrl && !form.leaderboardUrl.trim().startsWith("https://")) {
    //   next.leaderboardUrl = "Link must start with https://";
    // }
    return next;
  };

  /** Same rules as the backend; an empty field means that protection is off. */
  const validateProtections = (next) => {
    if (form.poolType === "DYNAMIC") {
      const growth = numberOrNull(form.poolGrowthPercent);
      if (growth === null) {
        next.poolGrowthPercent = "Enter what % of the bets goes into the pool";
      } else if (!(growth > 0 && growth <= 100)) {
        next.poolGrowthPercent = "Enter more than 0 and at most 100";
      }
    }
    const minTotal = numberOrNull(form.minTotalBet);
    if (minTotal === null || !(minTotal >= 0)) next.minTotalBet = "Enter 0 or more (0 = no minimum)";
    // Prize cap, winners % and pool milestones are switched off for now.
    // const cap = numberOrNull(form.maxPrizeMultiplier);
    // if (cap !== null && !(cap > 0)) next.maxPrizeMultiplier = "Enter more than 0";
    // const winnerPct = numberOrNull(form.winnerPercent);
    // if (winnerPct !== null && !(winnerPct > 0 && winnerPct <= 100)) next.winnerPercent = "Enter more than 0 and at most 100";
    //
    // const maxPool = parseFloat(form.prizePool);
    // const counts = new Set();
    // for (const tier of form.poolTiers) {
    //   const players = parseInt(tier.minPlayers, 10);
    //   const pool = parseFloat(tier.pool);
    //   if (!(players >= 0) || !(pool >= 0)) {
    //     next.poolTiers = "Fill both boxes of every milestone (0 or more), or remove it";
    //   } else if (maxPool > 0 && pool > maxPool) {
    //     next.poolTiers = "A milestone pool cannot be larger than the Prize Pool";
    //   } else if (counts.has(players)) {
    //     next.poolTiers = "Two milestones have the same player count";
    //   }
    //   counts.add(players);
    // }
  };

  const handleSave = () => {
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      // const protectionFields = ["minTotalBet", "maxPrizeMultiplier", "winnerPercent", "poolTiers"];
      // if (protectionFields.some((field) => validationErrors[field])) {
      //   setShowProtection(true);
      // }
      ErrorToast("Please fill the required fields");
      return;
    }

    const payload = {
      name: form.name.trim(),
      provider: form.provider.trim() || null,
      winMechanic: form.winMechanic,
      startDate: new Date(form.startDate).toISOString(),
      endDate: new Date(form.endDate).toISOString(),
      // Minimum bet per spin is no longer asked for.
      // minBet: form.minBet === "" ? null : parseFloat(form.minBet),
      minBet: null,
      prizePool: parseFloat(form.prizePool),
      winnerCount: parseInt(form.winnerCount, 10),
      prizeSteepness: parseFloat(form.prizeSteepness),
      leaderboardUrl: form.leaderboardUrl.trim() || null,
      bannerUrl: form.bannerUrl || null,
      isActive: form.isActive ? 1 : 0,
      poolGrowthPercent: form.poolType === "DYNAMIC" ? numberOrNull(form.poolGrowthPercent) : null,
      minTotalBet: numberOrNull(form.minTotalBet),
      // Prize cap, winners % and pool milestones are switched off for now.
      // maxPrizeMultiplier: numberOrNull(form.maxPrizeMultiplier),
      // winnerPercent: numberOrNull(form.winnerPercent),
      // poolTiers: form.poolTiers.length
      //   ? form.poolTiers.map((tier) => ({ minPlayers: parseInt(tier.minPlayers, 10), pool: parseFloat(tier.pool) }))
      //   : null,
      maxPrizeMultiplier: null,
      winnerPercent: null,
      poolTiers: null,
    };
    if (editData?.id) {
      payload.id = editData.id;
    }

    const url = editData?.id ? endPoints.api.UPDATE_TOURNAMENT : endPoints.api.CREATE_TOURNAMENT;
    setIsLoading(true);
    addData(url, payload)
      .then((response) => {
        setIsLoading(false);
        if (response?.data?.status === "success") {
          SuccesToast(response.data.message);
          onClose();
        } else {
          ErrorToast(response?.data?.message || "Unable to save tournament.");
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error(error);
        ErrorToast("Unable to save tournament.");
      });
  };

  /** Same upload the Banner screen uses; it returns the stored CDN path. */
  const uploadBanner = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setIsLoading(true);
    fetch(`${endPoints.apiBaseUrl}${endPoints.api.UPLOAD_BANNER}`, { method: "POST", body: formData })
      .then((response) => response.json())
      .then((result) => {
        setIsLoading(false);
        if (result?.data) {
          setField("bannerUrl", result.data);
        } else {
          ErrorToast(result?.message || "Unable to upload banner.");
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error(error);
        ErrorToast("Unable to upload banner.");
      });
  };

  // Prize split for the table; empty until pool, winners and steepness are all filled in.
  const maxPool = parseFloat(form.prizePool);
  const winnerCount = parseInt(form.winnerCount, 10);
  const steepness = parseFloat(form.prizeSteepness);
  const fullPoolRows = prizeTable(maxPool, winnerCount, steepness);

  // A dynamic pool is % of the bets so far, never more than the maximum (same as the backend).
  const isDynamic = form.poolType === "DYNAMIC";
  const growth = parseFloat(form.poolGrowthPercent) || 0;
  const poolNow = Math.floor(Math.min(maxPool || 0, (totalBets * growth) / 100) * 100) / 100;
  let prizeRows = fullPoolRows;
  if (isDynamic) {
    // Before anyone bets the pool is 0, so every prize is 0.
    prizeRows = poolNow > 0
      ? prizeTable(poolNow, winnerCount, steepness)
      : fullPoolRows.map((row) => ({ rank: row.rank, prize: 0 }));
  }

  const textField = (field, label, type = "text") => (
    <FormControl style={{ width: "100%", marginTop: "20px" }}>
      <TextField
        variant="outlined"
        label={label}
        type={type}
        value={form[field]}
        error={!!errors[field]}
        helperText={errors[field]}
        onChange={(event) => setField(field, event.target.value)}
      />
    </FormControl>
  );

  const dateField = (field, label) => (
    <FormControl style={{ width: "100%", marginTop: "20px" }}>
      <label>{label}</label>
      <TextField
        variant="outlined"
        type="datetime-local"
        value={form[field]}
        error={!!errors[field]}
        helperText={errors[field]}
        onChange={(event) => setField(field, event.target.value)}
      />
    </FormControl>
  );

  return (
    <Dialog maxWidth="sm" fullWidth open={open}>
      <DialogTitle style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
        {readOnly ? "View Tournament" : editData?.id ? "Edit Tournament" : "New Tournament"}
        <TournamentGuide label="Help" />
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && <Loader spinner={true} visible={isLoading} />}
        {readOnly && (
          <div style={{ fontSize: "13px", color: tokens.textDim }}>
            Results are saved, so these settings can only be read. See 🏆 Players &amp; Payout for ranks and prizes.
          </div>
        )}
        {/* A disabled fieldset locks every input inside it. */}
        <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>

        <Section number="1" title="Basics" hint="What it is called, when it runs and how players score." />
        {textField("name", "Tournament Name (e.g. Autumn Season of Legends - October)")}
        {/* Provider is no longer asked for; games are picked per provider in the Games dialog.
        {textField("provider", "Provider")} */}

        <Form.Group style={{ marginTop: "20px" }} controlId="tournamentWinMechanic">
          <Form.Label>Win Mechanic</Form.Label>
          <Form.Select
            value={form.winMechanic}
            isInvalid={!!errors.winMechanic}
            onChange={(event) => setField("winMechanic", event.target.value)}
          >
            <option value="">-- Select --</option>
            {WIN_MECHANICS.map((mechanic) => (
              <option key={mechanic} value={mechanic}>
                {mechanic}
              </option>
            ))}
          </Form.Select>
          {errors.winMechanic && (
            <Form.Control.Feedback type="invalid" style={{ display: "block" }}>
              {errors.winMechanic}
            </Form.Control.Feedback>
          )}
        </Form.Group>

        {dateField("startDate", "Start (your local time)")}
        {dateField("endDate", "End (your local time)")}
        {/* Minimum bet per spin is no longer asked for.
        {textField("minBet", "Minimum Bet per spin (SC, optional)", "number")} */}
        <FormControl style={{ width: "100%", marginTop: "20px" }}>
          <TextField
            variant="outlined"
            type="number"
            label="Minimum total bet to win (SC)"
            value={form.minTotalBet}
            error={!!errors.minTotalBet}
            helperText={
              errors.minTotalBet ||
              "A player must bet at least this much in total during the tournament to win a prize. 0 = no minimum."
            }
            onChange={(event) => setField("minTotalBet", event.target.value)}
          />
        </FormControl>

        <Section number="2" title="Prizes" hint="How much is paid and how it is split between the top players." />
        <Form.Group style={{ marginTop: "16px" }} controlId="tournamentPoolType">
          <Form.Label style={{ marginBottom: "4px" }}>Pool type</Form.Label>
          <Form.Check
            type="radio"
            id="poolTypeFixed"
            name="poolType"
            label="Fixed pool: players share exactly the amount below"
            checked={form.poolType === "FIXED"}
            onChange={() => setField("poolType", "FIXED")}
          />
          <Form.Check
            type="radio"
            id="poolTypeDynamic"
            name="poolType"
            label="Dynamic pool: grows with how much players bet, up to the amount below"
            checked={form.poolType === "DYNAMIC"}
            onChange={() => setField("poolType", "DYNAMIC")}
          />
        </Form.Group>
        {form.poolType === "DYNAMIC" && (
          <FormControl style={{ width: "100%", marginTop: "16px" }}>
            <TextField
              variant="outlined"
              type="number"
              label="% of all bets that goes into the pool"
              value={form.poolGrowthPercent}
              error={!!errors.poolGrowthPercent}
              helperText={
                errors.poolGrowthPercent ||
                "Example: 2 means players betting 50,000 SC in total make a 1,000 SC pool."
              }
              onChange={(event) => setField("poolGrowthPercent", event.target.value)}
            />
          </FormControl>
        )}
        {textField(
          "prizePool",
          form.poolType === "DYNAMIC" ? "Maximum pool (SC): the pool never grows past this" : "Prize Pool (SC)",
          "number"
        )}
        {textField("winnerCount", "Number of Winners", "number")}
        {textField(
          "prizeSteepness",
          "Prize Steepness (0 = equal, 0.75 = like Evoplay, 1 = default)",
          "number"
        )}
        <SteepnessHelp />
        <PrizeTable rows={prizeRows} />
        {prizeRows.length > 0 && (
          <div style={{ fontSize: "12px", opacity: 0.75, marginTop: "4px" }}>
            {isDynamic
              ? `Total bets by players so far: ${totalBets.toFixed(2)} SC, so the pool right now is ${poolNow.toFixed(
                  2
                )} SC (${growth}% of the bets, at most ${maxPool || 0} SC). It starts at 0 and grows with every counted bet.`
              : "Shows the split of the Prize Pool."}
          </div>
        )}

        {/* Protection settings are switched off for now.
        <Section
          number="3"
          title="Protection (optional)"
          hint="Stops a quiet tournament from paying the whole pool for very little play. You can skip this."
        />
        <Button variant="outline-secondary" size="sm" onClick={() => setShowProtection(!showProtection)}>
          {showProtection ? "Hide protection settings" : "Show protection settings"}
        </Button>
        {showProtection && <ProtectionFields form={form} setField={setField} errors={errors} />} */}
        {/* Leaderboard link is not asked for any more; the leaderboard is our own.
        {textField("leaderboardUrl", "Provider Leaderboard Link (optional, https://)")} */}

        <Section number="3" title="Look and visibility" hint="The picture on the tournament card, and whether players can see it." />
        <Form.Group style={{ marginTop: "16px" }} controlId="tournamentBanner">
          <Form.Label>Banner Image (optional)</Form.Label>
          {/* The upload endpoint only accepts JPG and PNG. */}
          <Form.Control
            type="file"
            accept="image/jpeg,image/png"
            isInvalid={!!errors.bannerUrl}
            onChange={uploadBanner}
          />
          {errors.bannerUrl && (
            <Form.Control.Feedback type="invalid" style={{ display: "block" }}>
              {errors.bannerUrl}
            </Form.Control.Feedback>
          )}
          {form.bannerUrl && (
            <img
              src={`${DefaultImageBaseurl}${form.bannerUrl}`}
              alt="Tournament banner"
              style={{ width: "100%", marginTop: "10px", borderRadius: "8px" }}
            />
          )}
        </Form.Group>

        <FormControlLabel
          control={
            <Switch checked={form.isActive} onClick={() => setField("isActive", !form.isActive)} />
          }
          labelPlacement="start"
          label="Active"
        />

        </fieldset>
        <TournamentSummary form={form} prizeRows={fullPoolRows} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{readOnly ? "Close" : "Cancel"}</Button>
        {!readOnly && <Button onClick={handleSave}>Save</Button>}
      </DialogActions>
    </Dialog>
  );
};

export default CreateTournament;
